import React, {useEffect, useState} from "react";
import {Box, Button, Checkbox, Grid, Typography} from "@mui/material";
import {useTranslation} from "react-i18next";

export default function CheckboxInput({
                                          data,
                                          addQuestion,
                                          deleteQuestionsAfterIndex,
                                          goToNextQuestion,
                                          isVisible,
                                          addToUpdateQuestions,
                                          deleteFromQuestionToUpdate,
                                      }) {
    const {t} = useTranslation();
    const language = localStorage.getItem("i18nextLng");
    const [selectedValues, setSelectedValues] = useState([]);

    // Load default selected values from `data.currentValue` if provided
    useEffect(() => {
        if (data?.currentValue) {
            setSelectedValues(data.currentValue);
        }
    }, [data]);

    // Handle checkbox selection/deselection
    const handleCheckboxChange = (value) => {
        deleteQuestionsAfterIndex(data);
        deleteFromQuestionToUpdate(data);
        if (selectedValues.includes(value)) {
            // If the value is already selected, remove it
            setSelectedValues(selectedValues.filter((val) => val !== value));
        } else {
            // Otherwise, add it to the selected list
            setSelectedValues([...selectedValues, value]);
        }
    };

    const handleNextClick = () => {
        // Update the backend with the selected values
        if (
            data &&
            selectedValues.length > 0 &&
            selectedValues !== data.currentValue
        ) {
            addToUpdateQuestions(data, selectedValues);
        }

        if (selectedValues.length > 1) {
            // Find all selected choices with `question_object`
            const selectedChoices = data.choices.filter(
                (choice) =>
                    selectedValues.includes(choice.value) && choice.question_object
            );

            if (selectedChoices.length > 0) {
                // Take the first question_object as the base
                let firstQuestion = {...selectedChoices[0].question_object};

                // Add the `question_object` of the second choice (if exists) to the first one
                if (selectedChoices.length > 1) {
                    const secondQuestionObject = selectedChoices[1].question_object;
                    if (secondQuestionObject) {
                        // Append the second question_object to the first one
                        firstQuestion.question_object = secondQuestionObject;
                    }
                }

                // Add the modified first question to the visibleQuestions state
                addQuestion(firstQuestion);
            }
        } else {
            // If only one value is selected, proceed normally
            selectedValues.forEach((value) => {
                const selectedChoice = data.choices.find(
                    (choice) => choice.value === value
                );
                if (selectedChoice?.question_object) {
                    addQuestion(selectedChoice.question_object);
                }
            });
        }
        // If no additional questions are added, move to the next question
        if (
            selectedValues.length === 0 ||
            !data.choices.some((choice) => choice.question_object)
        ) {
            goToNextQuestion();
        }
    };

    function handleSkipClick() {
        if (data?.associated_step_id === "4c2ea3bd-831a-4a53-8cc7-5ec1b26d628b") {
            if (data?.question_object) {

                addQuestion(data?.question_object);
            } else {
                // Otherwise, move to the next question
                goToNextQuestion(null, true);
            }
        } else {
            goToNextQuestion(null, true);
        }
    }

    return (
        <Box
            sx={{
                padding: "20px",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
            }}
        >
            {/* Question */}
            <Typography variant="subtitle1" sx={{mb: 2}}>
                {language === "fr" ? data?.question_fr : data.question}
            </Typography>

            {/* Checkbox Group */}
            <Grid container spacing={2} justifyContent="flex-start">
                {data?.choices.map((choice, index) => (
                    <Grid
                        item
                        key={`choice-${index}`}
                        xs={12}
                        sm={6}
                        md={4}
                        lg={3}
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                        }}
                    >
                        <Box
                            sx={{
                                border: `2px solid ${
                                    selectedValues.includes(choice.value) ? "#4caf95" : "#e0e0e0"
                                }`,
                                backgroundColor: selectedValues.includes(choice.value)
                                    ? "rgba(91,218,151,0.1)"
                                    : "#f7f7f7",
                                borderRadius: "10px",
                                padding: "10px 20px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "flex-start",
                                cursor: "pointer",
                                width: "100%",
                                transition: "all 0.3s ease",
                                "&:hover": {
                                    backgroundColor: selectedValues.includes(choice.value)
                                        ? "rgba(76,175,137,0.2)"
                                        : "#f0f0f0",
                                },
                            }}
                            onClick={() => handleCheckboxChange(choice.value)}
                        >
                            <Checkbox
                                checked={selectedValues.includes(choice.value)}
                                onChange={() => handleCheckboxChange(choice.value)}
                                sx={{
                                    color: "#4caf91",
                                    "&.Mui-checked": {
                                        color: "#4caf98",
                                    },
                                    "& .MuiSvgIcon-root": {
                                        fontSize: 28,
                                    },
                                }}
                            />
                            <Typography
                                variant="body1"
                                sx={{
                                    fontSize: "16px",
                                    fontWeight: "500",
                                    marginLeft: "10px",
                                    color: "#555",
                                }}
                            >
                                {t(choice.value)}
                            </Typography>
                        </Box>
                    </Grid>
                ))}
            </Grid>

            <div style={{display: "flex", gap: "10px", alignItems: "center"}}>
                {isVisible(data) && (
                    <Button
                        variant="contained"
                        color="secondary"
                        style={{
                            height: "50px",
                            width:
                                data?.associated_step_id !==
                                "4c2ea3bd-831a-4a53-8cc7-5ec1b26d628b"
                                    ? "50%"
                                    : "100%", // Each button takes 45% of the container width
                            marginTop: "20px",
                        }}
                        onClick={handleNextClick}
                        disabled={selectedValues.length === 0} // Disable if required fields are empty
                    >
                        {data?.last_step_question_2 ? t("finish") : t("next")}
                    </Button>
                )}
                {isVisible(data) &&
                    (
                        <Button
                            style={{
                                height: "50px",
                                width: "50%", // Each button takes 45% of the container width
                                marginTop: "20px",
                            }}
                            color="secondary"
                            variant="contained"
                            onClick={handleSkipClick}
                        >
                            {t("skip")}
                        </Button>
                    )}
            </div>
        </Box>
    );
}
